import { createTheme } from '@mantine/core'
import { THEMES_BY_ID, DEFAULT_THEME_ID, HIGH_CONTRAST_ID } from './registry'
import { normalizeThemeConfig } from './themeShape'

const DYSLEXIA_STACK = "'Atkinson Hyperlegible', 'Comic Sans MS', sans-serif"
const SIZE_SCALE = { small: 0.9, medium: 1, large: 1.2 }
const SIZE_FLOOR = { small: '1rem', medium: '1.0625rem', large: '1.1875rem' }

// Mantine config shared by every resolved theme (the parts that don't change
// per theme: radius, the spaceBlue palette, Paper/Button defaults). Per-theme
// mantine config is merged on top.
const BASE_MANTINE = {
  defaultRadius: 'md',
  colors: {
    spaceBlue: [
      '#e6f0ff', '#b3d1ff', '#80b3ff', '#4d94ff', '#1a75ff',
      '#0056e6', '#0044b3', '#003380', '#0a1628', '#060e1a',
    ],
  },
  components: {
    Paper: {
      defaultProps: {
        bg: 'var(--bw-panel-bg)',
        style: { border: '1px solid var(--bw-panel-border)' },
      },
    },
    Button: { defaultProps: { variant: 'light', color: 'cyan' } },
  },
}

/**
 * Compose the three orthogonal inputs into a live presentation.
 *
 * @param {Object} args
 * @param {string} [args.storyThemeId]  story.options.theme (admin-assigned)
 * @param {Object} [args.userA11y]      { high_contrast, reduced_motion,
 *                                        dyslexia_font, min_text_size }
 * @param {string} [args.sizePref]      'small' | 'medium' | 'large'
 * @param {Object} [args.customThemes]  id -> raw config (backend custom themes)
 * @returns {{ mantineTheme, cssVars, colorScheme, reducedMotion }}
 */
export function resolveTheme({
  storyThemeId,
  userA11y = {},
  sizePref = 'medium',
  customThemes = {},
} = {}) {
  // 1. Pick the base theme. High-contrast override wins; else the story's
  //    assigned theme (built-in or custom); else Novastar.
  let base
  if (userA11y.high_contrast) {
    base = THEMES_BY_ID[HIGH_CONTRAST_ID]
  } else if (storyThemeId && THEMES_BY_ID[storyThemeId]) {
    base = THEMES_BY_ID[storyThemeId]
  } else if (storyThemeId && customThemes[storyThemeId]) {
    base = normalizeThemeConfig({ id: storyThemeId, ...customThemes[storyThemeId] })
  } else {
    base = THEMES_BY_ID[DEFAULT_THEME_ID]
  }

  // 2. Clone cssVars and apply a11y modifiers.
  const cssVars = { ...base.cssVars }
  const reducedMotion = !!userA11y.reduced_motion || !!userA11y.high_contrast

  if (userA11y.dyslexia_font) {
    cssVars['--bw-body-font'] = DYSLEXIA_STACK
    cssVars['--bw-heading-font'] = DYSLEXIA_STACK
  }

  // min_text_size raises the per-theme base so even Small can't go below it.
  if (userA11y.min_text_size && SIZE_FLOOR[userA11y.min_text_size]) {
    const floor = parseFloat(SIZE_FLOOR[userA11y.min_text_size])
    const current = parseFloat(cssVars['--bw-content-base'] || '1.125')
    if (floor > current) cssVars['--bw-content-base'] = `${floor}rem`
  }

  // 3. Size pref -> content scale multiplier.
  cssVars['--bw-content-scale'] = String(SIZE_SCALE[sizePref] ?? 1)

  // 4. Build the Mantine theme: base + per-theme mantine, with the dyslexia
  //    font override also applied to Mantine's fontFamily so component text
  //    (not just markdown) follows.
  const mantineConfig = {
    ...BASE_MANTINE,
    ...base.mantine,
    components: { ...BASE_MANTINE.components, ...(base.mantine.components || {}) },
  }
  if (userA11y.dyslexia_font) {
    mantineConfig.fontFamily = DYSLEXIA_STACK
    mantineConfig.headings = { ...(mantineConfig.headings || {}), fontFamily: DYSLEXIA_STACK }
  }

  return {
    mantineTheme: createTheme(mantineConfig),
    cssVars,
    colorScheme: base.colorScheme || 'dark',
    reducedMotion,
  }
}
